import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext.js";
import { apiFetch } from "./api.js";

export interface ActiveCheckin {
  id: string;
  valdId: string;
  valdName: string;
  startedAt: string;
}

const EVENT = "lakseborsen:checkin-changed";

/**
 * The fisher's current "I'm fishing now" check-in. Shared across components
 * (banner in the layout, start button on the log-day page) via a window event,
 * so they stay in sync without a global store.
 */
export function useCheckin() {
  const { getToken, hasUsableSession } = useAuth();
  const [checkin, setCheckin] = useState<ActiveCheckin | null>(null);

  const refresh = useCallback(async () => {
    if (!hasUsableSession) {
      setCheckin(null);
      return;
    }
    try {
      const res = await apiFetch<{ checkin: ActiveCheckin | null }>(
        "/fishing-checkins/current",
        { getToken },
      );
      setCheckin(res.checkin);
    } catch {
      // Offline or signed out — show nothing rather than a stale banner.
      setCheckin(null);
    }
  }, [getToken, hasUsableSession]);

  useEffect(() => {
    refresh();
    window.addEventListener(EVENT, refresh);
    return () => window.removeEventListener(EVENT, refresh);
  }, [refresh]);

  const start = useCallback(
    async (valdId: string) => {
      await apiFetch("/fishing-checkins", {
        method: "POST",
        getToken,
        body: JSON.stringify({ valdId }),
      });
      window.dispatchEvent(new Event(EVENT));
    },
    [getToken],
  );

  const end = useCallback(async () => {
    await apiFetch("/fishing-checkins/current/end", {
      method: "POST",
      getToken,
    });
    window.dispatchEvent(new Event(EVENT));
  }, [getToken]);

  return { checkin, start, end, refresh };
}

/** "2 t 15 min" style elapsed time (language-neutral). */
export function formatElapsed(startedAt: string, now = Date.now()): string {
  const mins = Math.max(
    0,
    Math.floor((now - new Date(startedAt).getTime()) / 60000),
  );
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}` : `${m} min`;
}
